import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import U from '../../../felles/Utils';
import { searchStepArticles, previewStepArticleUpload, processStepArticleUpload } from '../../../felles/APICalls_NET';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Box,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';

// Type definitions
interface AppState {
  auth: {
    userJWT: {
      companyNr?: string;
      Department?: string;
      // andre properties...
    };
  };
}

interface SearchResult {
  firmaNr: string;
  articleId: string;
  edbNumber?: string;
}

interface PreviewRow {
  firmaNr: string;
  articleId: string;
  edbNumber: string;
  delete: boolean;
}

interface PreviewData {
  totalRows: number;
  rows: PreviewRow[];
  errors: string[];
}

interface UploadResult {
  totalProcessed: number;
  inserted: number;
  updated: number;
  deleted: number;
  errors: number;
  errorMessages: string[];
}

interface SearchRequest {
  companyNr: string;
  articleId?: string | null;
  edbNumber?: string | null;
}

type SearchType = 'stepId' | 'edbNumber';

const StepArticleManager: React.FC = () => {
  // 🔹 Hent firma fra Redux state
  const userJWT = useSelector((state: AppState) => state.auth.userJWT);
  const companyNr = userJWT?.companyNr || userJWT?.Department || ''; // Check both property names
  
  // Debug logging
  console.log('Redux state userJWT:', userJWT);
  console.log('Available userJWT properties:', userJWT ? Object.keys(userJWT) : 'userJWT is null/undefined');
  console.log('CompanyNr value:', companyNr);
  console.log('Department value (if exists):', userJWT?.Department);
  
  const [searchType, setSearchType] = useState<SearchType>('stepId');
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [recipientEmail, setRecipientEmail] = useState<string>('');

  // Search functionality
  const handleSearch = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      setError('Vennligst skriv inn søkeverdi');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults([]);
    setSearchPerformed(false);

    try {
      const searchRequest: SearchRequest = {
        companyNr: companyNr || '',
        articleId: searchType === 'stepId' ? searchValue : null,
        edbNumber: searchType === 'edbNumber' ? searchValue : null
      };

      const response = await searchStepArticles(searchRequest);
      setSearchResults(Array.isArray(response.data) ? response.data : []);
      setSearchPerformed(true);
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || 'Feil ved søk');
    } finally {
      setLoading(false);
    }
  };

  // File upload - automatic preview when file selected
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setPreviewData(null);
    setUploadResult(null);
    setError('');

    if (!selectedFile) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('companyNr', companyNr || '');

      console.log('Previewing upload with data:', {
        fileName: selectedFile.name,
        companyNr: companyNr,
        fileSize: selectedFile.size
      });

      const response = await previewStepArticleUpload(formData);
      setPreviewData(response.data);
    } catch (err: any) {
      console.error('Preview error:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.response?.data || 
                          err.message || 
                          'Feil ved forhåndsvisning';
      
      console.error('Detailed error info:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.baseURL
      });

      setError(`Server error (${err.response?.status || 'Unknown'}): ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };



  // File upload - process
  const handleProcess = async (): Promise<void> => {
    if (!file || !recipientEmail) {
      setError('Vennligst velg fil og skriv inn e-postadresse');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyNr', companyNr || '');
      formData.append('recipientEmail', recipientEmail);

      console.log('Processing upload with data:', {
        fileName: file.name,
        companyNr: companyNr,
        recipientEmail: recipientEmail
      });

      const response = await processStepArticleUpload(formData);
      setUploadResult(response.data);
      setPreviewData(null);
    } catch (err: any) {
      console.error('Process error:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.response?.data || 
                          err.message || 
                          'Feil ved behandling av fil';
      
      console.error('Detailed error info:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.baseURL
      });

      setError(`Server error (${err.response?.status || 'Unknown'}): ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTypeChange = (e: any): void => {
    setSearchType(e.target.value as SearchType);
    setSearchValue(''); // Nullstill søkefeltet alltid, også ved samme valg
    setSearchResults([]); // Nullstill tidligere søkeresultater også
    setSearchPerformed(false); // Reset søkestatus
  };

  const handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setRecipientEmail(e.target.value);
  };

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        mt: 1, 
        pr: 2, 
        pl: 1, 
        ml: 0,
        maxWidth: '1200px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
          {/* Header */}
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              mb: 1, 
              fontWeight: 400,
              color: '#333',
              //borderBottom: '2px solid #007bff',
              paddingBottom: '10px'
            }}
          >
            Søk og opplasting fil Step-id
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontSize: '16px',
              mb: 2,
              mt: 0
            }}
          >
            Søk etter Step-id og last opp filer for behandling.
          </Typography>

          {/* Nedlastingsknapper */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3, alignItems: { xs: 'stretch', sm: 'center' } }}
          >
            <Button
              variant="outlined"
              href={`${U.URL_ADDRESS}${U.HOMEPAGE_CONTEXT}/files/malStepID.csv`}
              download
              startIcon={<DescriptionIcon />}
              sx={{ 
                textTransform: 'none',
                borderColor: '#e0e0e0',
                color: '#666666',
                '&:hover': { 
                  borderColor: '#d32f2f',
                  backgroundColor: '#d32f2f',
                  color: 'white'
                }
              }}
            >
              Last ned mal
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                href={`${U.URL_ADDRESS}${U.HOMEPAGE_CONTEXT}/files/feltbeskrivelseStepID.xlsx`}
                download
                startIcon={<InfoOutlinedIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderColor: '#e0e0e0',
                  color: '#666666',
                  '&:hover': { 
                    borderColor: '#d32f2f',
                    backgroundColor: '#d32f2f',
                    color: 'white'
                  }
                }}
              >
                Feltforklaring
              </Button>
              
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {"Programmet brukes til å søke etter Step-id eller til å laste opp nye Step-id-er samt endringer.\n\nSøk på Step-id\nVelg søketype (EDB-nummer eller Step-id), og legg inn søket i feltet nedenfor.\n\nLast opp fil\nVelg riktig mal for opplasting av nye Step-id-er eller endringer. I malen skal du angi Step-id og EDB-nummer, samt registrere e-postadressen din for mottak av kvitteringsliste.\n\nViktig informasjon:\n•\t Step-id er master og du skal knytte edb-nummer mot Step-id\n• Dersom det allerede er registrert ett edb-nummer til Step-id, vil det nye edb-nummeret erstatte det eksisterende.\n• Hvis edb-nummer allerede er registrert på en annen Step-id, vil opplastingen bli avvist for å unngå dubletter.\n\nSletting av Step-id\nFor å slette en eksisterende Step-id, setter du S i siste kolonne i opplastingsmalen.\n\nHusk å kontrollere kvitteringslisten etter gjennomført opplasting"}
                  </Typography>
                }
                arrow
              >
                <InfoIcon style={{ cursor: "pointer", marginLeft: "10px", color: "#1d6f42" }} />
              </Tooltip>
            </Box>
          </Stack>
      
          {/* Search Section */}
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              mb: 2,
              bgcolor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 1.5, 
                display: 'flex', 
                alignItems: 'center',
                color: '#333',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              <SearchIcon sx={{ mr: 1 }} />
              Søk på registrert Step-id eller edbnr
            </Typography>
            <form onSubmit={handleSearch}>
              <Stack spacing={1.5}>
                <Box>
                  <FormControl fullWidth size="small" onClick={() => {
                    setSearchValue('');
                    setSearchResults([]);
                    setSearchPerformed(false);
                  }}>
                    <InputLabel>Søketype</InputLabel>
                    <Select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      label="Søketype"
                    >
                      <MenuItem value="stepId">Step-id</MenuItem>
                      <MenuItem value="edbNumber">EDB-nummer</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <TextField
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={searchType === 'stepId' ? 'Skriv inn Step-id' : 'Skriv inn EDB-nummer'}
                    label={searchType === 'stepId' ? 'Step-id' : 'EDB-nummer'}
                    fullWidth
                    size="small"
                  />
                </Box>

                <Box>
                  <Button 
                    type="submit" 
                    disabled={loading} 
                    variant="contained"
                    sx={{
                      bgcolor: '#d32f2f',
                      '&:hover': { bgcolor: '#b71c1c' },
                      textTransform: 'none'
                    }}
                  >
                    {loading ? 'Søker...' : 'Søk'}
                  </Button>
                </Box>
              </Stack>
            </form>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Søkeresultater ({searchResults.length})</Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto', overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Firmanr</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Step-id</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>EDB-nummer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((result: SearchResult, index: number) => (
                        <TableRow key={index} hover>
                          <TableCell>{result.firmaNr}</TableCell>
                          <TableCell>{result.articleId}</TableCell>
                          <TableCell>{result.edbNumber || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* No Results Message */}
            {searchPerformed && searchResults.length === 0 && !loading && !error && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                Ingen resultater funnet for "{searchValue}"
              </Typography>
            )}
          </Paper>

          {/* File Upload Section */}
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              mb: 2,
              bgcolor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 1.5, 
                display: 'flex', 
                alignItems: 'center',
                color: '#555',
                mt: 0,
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              <CloudUploadIcon sx={{ mr: 1 }} />
              Last opp fil
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Velg fil</Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    bgcolor: '#d32f2f',
                    '&:hover': { bgcolor: '#b71c1c' },
                    textTransform: 'none'
                  }}
                >
                  Velg Fil
                  <input
                    type="file"
                    accept=".txt,.csv"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Valgt fil:</strong> {file.name}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>E-post for kvitteringsliste</Typography>
                <TextField
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="din@epost.no"
                  fullWidth
                  size="small"
                />
              </Box>

              <Box>
                <Button 
                  onClick={handleProcess} 
                  disabled={loading || !file || !recipientEmail}
                  variant="contained" 
                  color="success" 
                  type="button"
                  sx={{ 
                    textTransform: 'none',
                    '&.Mui-disabled': {
                      backgroundColor: '#2e7d32',
                      color: 'white'
                    }
                  }}
                >
                  Send inn fil for oppdatering
                </Button>
              </Box>
            </Stack>

            {/* Preview Data */}
            {previewData && (
              <Paper elevation={1} sx={{ overflow: 'hidden', mt: 2, maxWidth: '100%' }}>
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">
                    Forhåndsvisning ({previewData.totalRows} rader)
                  </Typography>
                </Box>
                
                {previewData.errors && previewData.errors.length > 0 && (
                  <Box sx={{ p: 1.5, bgcolor: "#ffebee", borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Feil:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, wordBreak: 'break-word' }}>
                      {previewData.errors.map((err: string, index: number) => (
                        <Typography component="li" key={index} variant="body2" color="error">{err}</Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {previewData.rows && previewData.rows.length > 0 && (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', overflowX: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Firmanr</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Step-id</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>EDB-nummer</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Slett</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.rows.slice(0, 20).map((row: PreviewRow, index: number) => (
                          <TableRow 
                            key={index} 
                            hover
                            sx={row.delete ? { bgcolor: '#ffebee' } : {}}
                          >
                            <TableCell>{row.firmaNr}</TableCell>
                            <TableCell>{row.articleId}</TableCell>
                            <TableCell>{row.edbNumber}</TableCell>
                            <TableCell>{row.delete ? 'Ja' : 'Nei'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {previewData.rows && previewData.rows.length > 20 && (
                      <Box sx={{ p: 1, bgcolor: '#f8f9fa', borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">... og {previewData.rows.length - 20} flere rader</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Resultat</Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 1, bgcolor: "#e8f5e8" }}>
                  <Typography variant="body2"><strong>Totalt behandlet:</strong> {uploadResult.totalProcessed}</Typography>
                  <Typography variant="body2"><strong>Innsatt:</strong> {uploadResult.inserted}</Typography>
                  <Typography variant="body2"><strong>Oppdatert:</strong> {uploadResult.updated}</Typography>
                  <Typography variant="body2"><strong>Slettet:</strong> {uploadResult.deleted}</Typography>
                  <Typography variant="body2"><strong>Feil:</strong> {uploadResult.errors}</Typography>
                </Paper>

                {uploadResult.errorMessages && uploadResult.errorMessages.length > 0 && (
                  <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: "#ffebee" }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Feilmeldinger:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {uploadResult.errorMessages.map((err: string, index: number) => (
                        <Typography component="li" key={index} variant="body2" color="error">{err}</Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                <Typography variant="body2" sx={{ color: "green" }}>
                  ✓ Kvittering sendt til {recipientEmail}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Error Display */}
          {error && (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 1.5, 
                mb: 2, 
                bgcolor: "#ffebee",
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Typography variant="body2" sx={{ color: "red" }}>
                {error}
              </Typography>
            </Paper>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default StepArticleManager;